import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePostHog } from '@posthog/react'
import { getSessionAuth } from '../utils/sessionAuth'
import { getBillingStatus } from '../api/user'
import { ensureSessionUserId } from '../utils/sessionUser'
import '../style/views/PaymentsView.css'

const MAX_POLL_ATTEMPTS = 10
const POLL_INTERVAL_MS = 2000

type PaymentResultViewProps = {
  variant: 'success' | 'cancel'
  onBillingActive?: () => void
  onLogout?: () => void
}

function PaymentResultView({ variant, onBillingActive, onLogout }: PaymentResultViewProps) {
  const posthog = usePostHog()
  const navigate = useNavigate()
  const isSuccess = variant === 'success'
  const [pollError, setPollError] = useState(false)

  const handleRetry = () => {
    setPollError(false)
    window.location.reload()
  }

  useEffect(() => {
    if (!isSuccess) {
      onLogout?.()
      navigate('/', { replace: true })
      return
    }

    let attempt = 0
    let cancelled = false
    let timer: number

    const poll = async () => {
      if (cancelled) return
      attempt++

      try {
        const userId = await ensureSessionUserId(getSessionAuth())
        if (!userId || cancelled) return

        const data = await getBillingStatus(userId)
        const isActive =
          data.billingExempt === true ||
          data.subscriptionStatus === 'active' ||
          data.subscriptionStatus === 'trialing'

        if (isActive && !cancelled) {
          posthog?.capture('subscription_activated', {
            subscription_status: data.subscriptionStatus ?? 'billing_exempt',
            poll_attempt: attempt,
          })
          onBillingActive?.()
          navigate('/', { replace: true })
          return
        }
      } catch (err) {
        posthog?.captureException(err, { context: 'payment_result_poll' })
        // continue polling
      }

      if (!cancelled && attempt < MAX_POLL_ATTEMPTS) {
        timer = window.setTimeout(poll, POLL_INTERVAL_MS)
      } else if (!cancelled) {
        setPollError(true)
      }
    }

    // Brief initial delay to give the webhook time to fire
    timer = window.setTimeout(poll, 1000)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [isSuccess, navigate, onBillingActive, posthog])

  if (isSuccess) {
    return (
      <div className="payment-result-shell">
        <div className="paywall-brand-pill payment-result-brand-pill">
          <img src="/logo.svg" alt="" className="paywall-pill-logo" />
          <img src="/wordmark.png" alt="Scribbl" className="paywall-pill-wordmark" />
        </div>

        <div className="payment-result-card card">
          {pollError ? (
            <>
              <div className="payment-result-icon pending" aria-hidden="true">
                <span className="payment-result-icon-dot" />
              </div>
              <div className="payment-result-badge cancel">Subscription pending</div>
              <h1>We&apos;re still syncing your subscription</h1>
              <p className="muted payment-card-copy">
                Your payment may still be processing. Try again in a moment or sign out if you need to switch accounts.
              </p>
              <div className="payment-result-actions">
                <button type="button" className="primary-button payment-result-button" onClick={handleRetry}>
                  Try again
                </button>
                {onLogout ? (
                  <button type="button" className="paywall-signout-button payment-result-signout" onClick={onLogout}>
                    Sign out
                  </button>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <div className="payment-result-icon success" aria-hidden="true">
                <span className="payment-result-ring" />
                <span className="payment-result-check" />
              </div>
              <div className="payment-result-badge success">Payment received</div>
              <h1>Activating your Scribbl workspace</h1>
              <p className="muted payment-card-copy">
                We&apos;re confirming your subscription and opening your account.
              </p>
              <div className="payment-result-steps" aria-label="Subscription activation progress">
                <span>Payment confirmed</span>
                <span>Subscription syncing</span>
                <span>Opening Scribbl</span>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  // Cancel: show briefly while redirecting
  return (
    <div className="payment-result-shell">
      <div className="paywall-brand-pill payment-result-brand-pill">
        <img src="/logo.svg" alt="" className="paywall-pill-logo" />
        <img src="/wordmark.png" alt="Scribbl" className="paywall-pill-wordmark" />
      </div>
      <div className="payment-result-card card">
        <div className="payment-result-icon pending" aria-hidden="true">
          <span className="payment-result-icon-dot" />
        </div>
        <div className="payment-result-badge cancel">Checkout cancelled</div>
        <h1>No charge was completed</h1>
        <p className="muted payment-card-copy">Returning to plans&hellip;</p>
      </div>
    </div>
  )
}

export default PaymentResultView
